import {
    ClockCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    GlobalOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import {
    Button,
    DatePicker,
    Form,
    Input,
    Modal,
    notification,
    Popconfirm,
    Select,
    Skeleton,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import CustomTable from "../common/Table";
import { timeZoneOptions } from "../../services/util";
import { TeamMangementContext } from ".";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const { Option } = Select;

function Shift() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [formData, setFormData] = useState({ staff_id, page: 0, limit: 10 });
    const [shiftData, setShiftData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalTitle, setModalTitle] = useState("Add Shift");
    const [form] = Form.useForm();
    const { shiftFormVisible, setShiftFormVisible } =
        useContext(TeamMangementContext);

    const {
        status: shiftStatus,
        data: shiftRes,
        refetch: getShift,
    } = useQuery(
        "shift",
        async () => {
            return await HTTP("/teams/getShifts", { ...formData });
        },
        { enabled: false },
    );

    const neutralActionButtonStyle = {
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: 7,
        width: 30,
        height: 30,
    };

    const tintedActionButtonStyle = {
        background: "var(--color-error-light)",
        border: "1px solid var(--color-border)",
        borderRadius: 7,
        width: 30,
        height: 30,
    };

    const renderEmpty = (label = "Not available") => (
        <span className="team-management-empty">{label}</span>
    );

    const renderShiftIdentity = (_, record) => (
        <div className="team-management-identity">
            <div className="team-management-avatar">
                <ClockCircleOutlined />
            </div>
            <div className="team-management-identity-copy">
                <span className="team-management-identity-title">
                    {record.name || "Unnamed shift"}
                </span>
                <span className="team-management-identity-subtitle">
                    Shift ID {record.id}
                </span>
            </div>
        </div>
    );

    const renderChip = (label, type = "primary", emptyLabel = "Not configured") =>
        label ? (
            <span className={`team-management-chip team-management-chip-${type}`}>
                {label}
            </span>
        ) : (
            renderEmpty(emptyLabel)
        );

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: () => {},
            render: (_, record) => renderShiftIdentity(_, record),
        },
        {
            title: "Timing",
            key: "timing",
            render: (text, record) => {
                return record.timings_from && record.timings_to ? (
                    <span className="team-management-chip team-management-chip-accent">
                        {`${record.timings_from} - ${record.timings_to}`}
                    </span>
                ) : (
                    renderEmpty("Shift timing not set")
                );
            },
        },
        {
            title: "Time Zone",
            dataIndex: "time_zone",
            key: "time_zone",
            sorter: () => {},
            render: (value) =>
                renderChip(value, "neutral", "Time zone not selected"),
        },
        {
            title: "Action",
            key: "action",
            width: 96,
            render: (text, record) => {
                return (
                    <div className="team-management-action-row">
                        <Popconfirm
                            title="Delete this shift?"
                            onConfirm={() => deleteShift(record.id)}
                            icon={
                                <QuestionCircleOutlined
                                    style={{ color: "var(--color-error)" }}
                                />
                            }
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={
                                    <DeleteOutlined
                                        style={{ color: "var(--color-error)" }}
                                    />
                                }
                                style={tintedActionButtonStyle}
                            />
                        </Popconfirm>
                        <Button
                            type="text"
                            size="small"
                            icon={
                                <EditOutlined
                                    style={{ color: "var(--color-primary)" }}
                                />
                            }
                            onClick={() => openEditForm(record)}
                            style={neutralActionButtonStyle}
                        />
                    </div>
                );
            },
        },
    ];

    async function deleteShift(id) {
        const { success, message } = await HTTP("/teams/deleteShift", {
            staff_id,
            id,
        });
        let type = "error";
        if (success) {
            type = "info";
        }
        notification[type]({ message });
        getShift();
    }

    function openEditForm(record) {
        const timing = [
            dayjs(record.timings_from, "HH:mm"),
            dayjs(record.timings_to, "HH:mm"),
        ];
        form.setFieldsValue({
            name: record.name,
            timing,
            timings_from: dayjs(record.timings_from, "HH:mm"),
            timings_to: dayjs(record.timings_to, "HH:mm"),
            id: record.id,
            time_zone: record.time_zone,
        });
        setModalTitle("Edit Shift");
        setShiftFormVisible(true);
    }

    async function handleSubmit(values) {
        // [values.timings_from, values.timings_to] = values.timing;
        values.timings_to = dayjs(values.timings_to).format("HH:mm");
        values.timings_from = dayjs(values.timings_from).format("HH:mm");
        delete values.timing;
        let url = "/teams/addShift";
        if (values.id) {
            url = "/teams/updateShift";
        }
        const { success, message } = await HTTP(url, {
            staff_id,
            ...values,
        });
        let type = "error";
        if (success) {
            type = "info";
        }
        notification[type]({ message });
        closeForm();
        getShift();
    }

    function closeForm() {
        setShiftFormVisible(false);
        setModalTitle("Add Shift");
        form.resetFields();
    }

    useEffect(() => {
        getShift();
    }, []);

    useEffect(() => {
        getShift();
    }, [formData]);

    useEffect(() => {
        if (shiftStatus === "success") {
            const { data, total } = shiftRes;
            setShiftData(data);
            setTotalRecords(total);
        }
    }, [shiftStatus, shiftRes]);

    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        }
        setFormData({ ...formData, ...others });
    }

    return (
        <>
            {shiftStatus !== "idle" && shiftStatus !== "loading" ? (
                <CustomTable
                    columns={columns}
                    tableData={shiftData}
                    noOfrecords={totalRecords}
                    handleTableChange={handleTableChange}
                    rowKey="id"
                    isScroll
                />
            ) : (
                <Skeleton active title={false} paragraph={{ rows: 10 }} />
            )}

            <Modal
                open={shiftFormVisible}
                title={
                    <div className="team-management-modal-header">
                        <div className="team-management-modal-title">
                            {modalTitle}
                        </div>
                        <div className="team-management-modal-subtitle">
                            Create or update shift timings and the timezone used
                            by the team.
                        </div>
                    </div>
                }
                footer={null}
                className="team-management-modal"
                width={760}
                destroyOnHidden
                onCancel={closeForm}
            >
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    className="team-management-form"
                >
                    <Form.Item name="id" className="hidden">
                        <Input id="id" disabled />
                    </Form.Item>
                    <div className="team-management-form-section">
                        <div className="team-management-form-section-title">
                            Shift Details
                        </div>
                        <div className="team-management-form-grid">
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please add the shift name",
                                    },
                                ]}
                            >
                                <Input
                                    id="name"
                                    prefix={<ClockCircleOutlined />}
                                    placeholder="Enter shift name"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Time Zone"
                                name="time_zone"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a time zone",
                                    },
                                ]}
                            >
                                <Select
                                    id="time_zone"
                                    placeholder="Select time zone"
                                    suffixIcon={<GlobalOutlined />}
                                >
                                    {timeZoneOptions.map((e) => (
                                        <Option key={e} value={e}>
                                            {e}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    </div>
                    <div className="team-management-form-section">
                        <div className="team-management-form-section-title">
                            Shift Timing
                        </div>
                        <div className="team-management-form-grid">
                            <Form.Item
                                name="timings_from"
                                label="From"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a start time",
                                    },
                                ]}
                            >
                                <DatePicker
                                    allowClear={false}
                                    id="timings_from"
                                    picker="time"
                                    format={"HH:mm"}
                                    className="team-management-time-picker"
                                />
                            </Form.Item>
                            <Form.Item
                                name="timings_to"
                                label="To"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select an end time",
                                    },
                                ]}
                            >
                                <DatePicker
                                    allowClear={false}
                                    id="timings_to"
                                    picker="time"
                                    format={"HH:mm"}
                                    className="team-management-time-picker"
                                />
                            </Form.Item>
                        </div>
                    </div>
                    <div className="team-management-form-actions">
                        <Button onClick={closeForm}>Cancel</Button>
                        <Button type="primary" htmlType="submit">
                            Save Shift
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}
export default Shift;
